import Vue from 'vue'
import { MutationTree, Dictionary } from 'vuex'
import {
    EDITOR_INIT,
    EDITOR_CLEAR,
    EDITOR_SET_SCHEMA,
    EDITOR_SET_DATA,
    EDITOR_SET_FIELD_DATA,
    EDITOR_SET_VAULT,
    EDITOR_SET_TAG_DATA,
    EDITOR_SET_ERRORS,
    EDITOR_TOGGLE_TOP_LEVEL,
    EDITOR_ADD_TAG,
    EDITOR_RESTORE_FIELD,
    EDITOR_MARK_SYNCING,
    EDITOR_MARK_SYNCED,
    EDITOR_RESET_STATUSES,
    EDITOR_SET_SHARING,
    EDITOR_SET_TOP_LEVEL,
    EDITOR_SET_TAG_OPTIONS,
    EDITOR_SET_ICON
} from '@/store/editor/constants'
import { EditorState, EntryErrorSet } from '@/store/editor/types'
import Entry from '@/data/entry-model'

const mutations: MutationTree<EditorState> = {
    /**
     * Initialize the default stencil
     */
    [EDITOR_INIT]({ entry, stencils }) {
        const stencil = stencils[entry.schema]
        // change icon only if not already changed
        if (entry.icon === undefined || entry.icon === stencils[entry.schema].defaultIcon) {
            entry.icon = stencil.defaultIcon
        }
        let position = 0
        Object.keys(stencil.fields).forEach((key) => {
            Vue.set(entry.fields, key, { ...stencil.fields[key] })
            // create input object if empty
            if (!entry.fields[key].input) {
                Vue.set(entry.fields[key], 'input', {})
            }
            // add non-meta attributes
            Vue.set(entry.fields[key], 'status', 'unchanged')
            if (!entry.fields[key].value) {
                Vue.set(entry.fields[key], 'value', '')
            }
            Vue.set(entry.fields[key], 'error', undefined)
            Vue.set(entry.fields[key], 'position', position)
            position += 1
        })
    },

    /**
     * Empty all current editor values
     */
    [EDITOR_CLEAR](state) {
        const { entry, stencils } = state
        state.blank = true
        entry.id = undefined
        entry.existing = false
        entry.permission = 'private'
        entry.locked = false
        entry.favourite = false
        entry.vault = undefined
        entry.tags = []
        entry.icon = stencils[entry.schema].defaultIcon
        entry.topLevel = false
        entry.vaultError = undefined
        Object.keys(entry.fields).forEach((key: string) => {
            const field = entry.fields[key]
            field.value = stencils[entry.schema].fields[key].value
            field.status = stencils[entry.schema].fields[key].status
        })
        entry.model = undefined
    },

    /**
     * Set the editor schema. Does not do anything
     * if not a new entry (entry.existing = false)
     */
    [EDITOR_SET_SCHEMA](state, schema: string) {
        const { entry, stencils } = state
        state.blank = false
        const stencil = stencils[schema]
        if (stencil !== undefined && !entry.existing) {
            // change icon only if not already changed
            if (entry.icon === undefined || entry.icon === stencils[entry.schema].defaultIcon) {
                entry.icon = stencil.defaultIcon
            }
            // persist values from editor
            const preserve: Dictionary<String> = {}
            // kill old fields that are not in new stencil
            Object.keys(entry.fields).forEach((key) => {
                if (entry.fields[key].value !== undefined
                    && Object.keys(stencil.fields).includes(key)) {
                    preserve[key] = entry.fields[key].value!
                }
                delete entry.fields[key]
            })
            let position = 0
            Object.keys(stencil.fields).forEach((key) => {
                Vue.set(entry.fields, key, { ...stencil.fields[key] })
                // create input object if empty
                if (!entry.fields[key].input) {
                    Vue.set(entry.fields[key], 'input', {})
                }
                // add non-meta attributes
                Vue.set(entry.fields[key], 'status', 'unchanged')
                if (!entry.fields[key].value) {
                    Vue.set(entry.fields[key], 'value', '')
                }
                if (preserve[key] !== undefined) {
                    Vue.set(entry.fields[key], 'value', preserve[key])
                }
                Vue.set(entry.fields[key], 'error', undefined)
                Vue.set(entry.fields[key], 'position', position)
                position += 1
            })
            entry.schema = schema
        }
    },

    /**
     * Copy data from a Vault or Login into the editor
     */
    [EDITOR_SET_DATA]({ entry }, model: Entry) {
        entry.id = model.id
        entry.schema = model.schemaType
        entry.documentKey = model.documentKey
        entry.permission = model.permission
        entry.existing = true
        entry.locked = true
        entry.topLevel = model.isTopLevel()
        entry.vault = model.parentId
        entry.tags = model.tags
        entry.icon = model.icon
        Object.keys(entry.fields).forEach((key: string) => {
            entry.fields[key].value = model.fields[key]
        })
        entry.fields.title.value = model.title
        entry.model = model
    },

    /**
     * Set a field value
     */
    [EDITOR_SET_FIELD_DATA]({ entry }, { key, value }: { key: string, value: string }) {
        if (entry.fields[key].value !== value) {
            if (entry.existing && entry.fields[key].status === 'unchanged') {
                Vue.set(entry.fields[key], 'initial', entry.fields[key].value)
                entry.fields[key].status = 'changed'
            }
            entry.fields[key].value = value
            entry.fields[key].error = undefined
        }
    },

    /**
     * Set the vault ID
     */
    [EDITOR_SET_VAULT]({ entry }, vault: string) {
        entry.vault = vault
        entry.vaultError = undefined
    },

    /**
     * Set the icon for the entry
     */
    [EDITOR_SET_ICON]({ entry }, icon: string) {
        entry.icon = icon
    },

    /**
     * Set tag list
     */
    [EDITOR_SET_TAG_DATA]({ entry }, tags: string[]) {
        entry.tags = tags
        entry.tagError = undefined
    },

    /**
     * Set errors
     */
    [EDITOR_SET_ERRORS]({ entry }, errors: EntryErrorSet) {
        entry.vaultError = errors.vault
        entry.tagError = errors.tags
        Object.keys(errors.fields).forEach((key) => {
            entry.fields[key].error = errors.fields[key]
        })
    },

    /**
     * Enable / disable top level
     */
    [EDITOR_TOGGLE_TOP_LEVEL]({ entry }) {
        entry.topLevel = !entry.topLevel
        if (entry.topLevel) {
            entry.vault = undefined
        }
        entry.vaultError = undefined
    },


    /**
     * Enable / disable top level
     */
    [EDITOR_SET_TOP_LEVEL]({ entry }, topLevel) {
        entry.topLevel = topLevel
        if (topLevel) {
            entry.vault = undefined
        }
        entry.vaultError = undefined
    },

    /**
     * Add a new tag to the options list
     */
    [EDITOR_ADD_TAG]({ tagOptions }, tag: string) {
        tagOptions.push(tag)
    },

    [EDITOR_SET_TAG_OPTIONS](state, options: string[]) {
        state.tagOptions = options
    },

    /**
     * Restore a field to its initial value
     */
    [EDITOR_RESTORE_FIELD]({ entry }, key: string) {
        if (entry.fields[key].initial) {
            entry.fields[key].value = entry.fields[key].initial
        }
        entry.fields[key].status = 'unchanged'
    },

    /**
     * Mark all changed fields as syncing
     */
    [EDITOR_MARK_SYNCING]({ entry }) {
        Object.keys(entry.fields).forEach((key) => {
            if (entry.fields[key].status === 'changed') {
                Vue.set(entry.fields[key], 'initial', undefined)
                entry.fields[key].status = 'syncing'
            }
        })
    },

    /**
     * Mark all syncing fields as synced
     */
    [EDITOR_MARK_SYNCED]({ entry }) {
        Object.keys(entry.fields).forEach((key) => {
            if (entry.fields[key].status === 'syncing') {
                entry.fields[key].status = 'synced'
            }
        })
    },

    /**
     * Mark all synced fields as unchanged
     */
    [EDITOR_RESET_STATUSES]({ entry }) {
        Object.keys(entry.fields).forEach((key) => {
            if (entry.fields[key].status === 'synced') {
                entry.fields[key].status = 'unchanged'
            }
        })
    },

    /**
     * Enable/disable sharing
     */
    [EDITOR_SET_SHARING]({ entry }, sharing: boolean) {
        entry.permission = sharing ? 'creator' : 'private'
    }
}

export default mutations
